import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CarIcon, CheckIcon, HeartHandshakeIcon, MessageCircleIcon, SendIcon, StarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/Button';
import { Chip } from './ui/Chip';
import { FitBadge } from './ui/FitBadge';
import { Tag } from './ui/Tag';
import { useMarketplace } from '../contexts/MarketplaceContext';
import { budgetLabel, travelCost } from '../utils/event';
import { eventFit } from '../utils/fit';
import { baht, formatDate } from '../utils/format';
import { spring } from '../utils/motion';
import type { MusicEvent } from '../types/marketplace';
interface EventDetailProps {
  event: MusicEvent;
  onLimit: () => void;
  onDone?: () => void;
}
export function EventDetail({
  event,
  onLimit,
  onDone
}: EventDetailProps) {
  const {
    me,
    applied,
    saved,
    toggleSave,
    applyToEvent,
    performerPrefs,
    hasMatch
  } = useMarketplace();
  const navigate = useNavigate();
  const [composing, setComposing] = useState(false);
  const [note, setNote] = useState('');
  const suggested = useMemo(() => me.keys.filter(k => event.genres.includes(k.genre)).map(k => k.id), [me.keys, event.genres]);
  const [setlist, setSetlist] = useState<string[]>(suggested);
  const fit = eventFit(event, performerPrefs, me);
  const travel = travelCost(me, event.distanceKm);
  const matched = hasMatch(event.id, me.id);
  const booked = event.slots.some(s => s.filledBy === me.id);
  const isApplied = applied.includes(event.id);
  const isSaved = saved.includes(event.id);
  const submit = () => {
    const songs = me.keys.filter(k => setlist.includes(k.id)).map(k => k.title);
    const result = applyToEvent(event.id, note || (songs.length ? `Suggested setlist: ${songs.join(', ')}` : ''));
    if (result === 'limit') return onLimit();
    if (result === 'applied') toast.success(`Applied to ${event.name}`, {
      description: 'We’ll let you know when the organiser responds.'
    });
    setComposing(false);
    onDone?.();
  };
  return <div className="pb-2" data-id="element-212" data-mp="108l06zdr5v5w">
      <div className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5" data-id="element-213" data-mp="1094abtx5vf9d">
        {event.photos.map((p, i) => <img key={p + i} src={p} alt={i === 0 ? event.name : ''} className="aspect-[4/3] w-[88%] shrink-0 snap-center rounded-md object-cover" data-id="element-214" data-mp="qhqa1jxdybf6" data-mpi={i} />)}
      </div>

      <div className="mt-5 flex items-start justify-between gap-3" data-id="element-215" data-mp="1j5m7wryoy9dj">
        <div data-id="element-216" data-mp="149mzfg7o677v">
          <h2 className="text-[26px] font-bold leading-tight tracking-[-0.015em]" data-id="element-217" data-mp="14a6969n61pat">{event.name}</h2>
          <p className="mt-1 text-[15px] text-ink-2" data-id="element-218" data-mp="197oku7skiimi">by {event.organiser}</p>
        </div>
        <FitBadge score={fit.score} onPhoto={false} data-id="element-219" data-mp="3oz42y5rwevrh" />
      </div>
      <p className="mt-3 flex items-center gap-2 text-sm text-ink-2" data-id="element-220" data-mp="3p1q5ts3ugn3d">
        <span className="h-1.5 w-1.5 rounded-full bg-brand" aria-hidden="true" data-id="element-221" data-mp="3u09vo3lk6j10" />
        {fit.reason}
      </p>

      <dl className="mt-6 grid grid-cols-2 gap-x-6 border-y border-line py-4 text-[15px]" data-id="element-222" data-mp="2bz8xiiuzl7r0">
        {[['When', `${formatDate(event.date, 'EEE d MMM yyyy')} · ${event.startTime}`], ['Where', `${event.venue}, ${event.city.split(',')[0]}`], ['Budget per act', budgetLabel(event)], ['Crowd', `${event.capacity.toLocaleString('en-GB')} guests`], ['Theme', event.theme], ['Type', event.type]].map(([k, v]) => <div key={k} className="py-2" data-id="element-223" data-mp="1ocsmn8ff5qdo">
            <dt className="text-[13px] font-semibold text-ink-3" data-id="element-224" data-mp="1o9ocluf9w0f3">{k}</dt>
            <dd className="mt-0.5 font-bold" data-id="element-225" data-mp="224777joz5zmu">{v}</dd>
          </div>)}
      </dl>

      <p className="mt-5 text-[16px] leading-relaxed text-ink-2" data-id="element-226" data-mp="1xami6h5d0fzs">{event.description}</p>
      <div className="mt-4 flex flex-wrap gap-1.5" data-id="element-227" data-mp="19ljtipcb5mvs">
        {event.genres.map(g => <Tag key={g} data-id="element-228" data-mp="15jyil9jc3io8">{g}</Tag>)}
      </div>

      <h3 className="mt-7 text-lg font-bold" data-id="element-229" data-mp="15j6wcpbmmxam">Line-up</h3>
      <ul className="mt-2 divide-y divide-line" data-id="element-230" data-mp="1ibjyneor7qss">
        {event.slots.map(s => <li key={s.id} className="flex items-center justify-between gap-3 py-3" data-id="element-231" data-mp="1nadevevfdvwn">
            <div data-id="element-232" data-mp="1s7wqs84nhwlf">
              <p className="font-bold" data-id="element-233" data-mp="1s7dk5nkgaump">{s.kind}</p>
              <p className="text-sm text-ink-3" data-id="element-234" data-mp="1s6lne9f4aquu">{s.setLength} min set</p>
            </div>
            <div className="flex items-center gap-3" data-id="element-235" data-mp="1x17yscl78jex">
              <span className="tabular font-bold" data-id="element-236" data-mp="21x6llvqvptd8">{baht(s.budget)}</span>
              {s.filledBy ? <Tag data-id="element-237" data-mp="25z1x1t2ixn4q">{s.filledBy === me.id ? 'You' : 'Filled'}</Tag> : <Tag tone="brand" data-id="element-238" data-mp="261wfjf1z8xvi">Open</Tag>}
            </div>
          </li>)}
      </ul>

      <div className="mt-5 flex items-center gap-3 rounded-md bg-surface-2 px-4 py-3.5" data-id="element-239" data-mp="2awtmmgfp4x5r">
        <CarIcon className="h-5 w-5 shrink-0 text-ink-2" data-id="element-240" data-mp="2fx7vg0p4gb4u" />
        <p className="text-sm leading-snug text-ink-2" data-id="element-241" data-mp="glop7tv7dgyv">
          Your travel ≈ <span className="tabular font-bold text-ink" data-id="element-242" data-mp="ljyly8wkspml">{baht(travel)}</span> · {event.distanceKm * 2} km round trip at ฿{me.travelCostPerKm}/km
        </p>
      </div>

      <div className="mt-6" data-id="element-243" data-mp="e1rz6bed077n">
        {booked ? <StatusBlock icon={<CheckIcon className="h-5 w-5" data-id="element-245" data-mp="98peksrk883l" />} title="You’re booked" body="See the details in Gigs → Booked." data-id="element-244" data-mp="9977pgixe67a" /> : matched ? <div className="space-y-3" data-id="element-246" data-mp="48jjf2m303gr">
            <StatusBlock icon={<HeartHandshakeIcon className="h-5 w-5" data-id="element-248" data-mp="3vhqewd3qdars" />} title="You’re matched" body="Talk through the details and wait for an offer." data-id="element-247" data-mp="3vhgx3csua9x6" />
            <Button variant="primary" fullWidth icon={<MessageCircleIcon className="h-4 w-4" data-id="element-250" data-mp="s0hzs10e0p0r" />} onClick={() => navigate(`/p/messages/m-${event.id}-${me.id}`)} data-id="element-249" data-mp="wyjp33oepkko">
              Open chat
            </Button>
          </div> : isApplied ? <StatusBlock icon={<SendIcon className="h-5 w-5" data-id="element-252" data-mp="i10tpih84949" />} title="Application sent" body="The organiser will review your profile. You’ll match if they say yes." data-id="element-251" data-mp="i2kb5cem8q7m" /> : <AnimatePresence mode="wait" initial={false} data-id="element-253" data-mp="32s9o1jvre1xo">
            {composing ? <motion.div key="compose" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 12
        }} transition={spring.smooth} className="space-y-4" data-id="element-254" data-mp="32umfcnnepone">
                <label className="block" data-id="element-255" data-mp="1ytmpv4jccp3f">
                  <span className="text-[15px] font-bold" data-id="element-256" data-mp="1kv6a6o3i3o95">Note to the organiser</span>
                  <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Say why you’re a great fit…" className="mt-2 w-full resize-none rounded-sm border border-line bg-surface px-4 py-3 text-[15px] placeholder:text-ink-3 focus:border-ink focus:outline-none" data-id="element-257" data-mp="1ptreaylvis2v" />
                </label>
                <div data-id="element-258" data-mp="174b7xunic5ej">
                  <p className="text-[15px] font-bold" data-id="element-259" data-mp="173rtinx2fz59">Suggested setlist</p>
                  <p className="text-sm text-ink-3" data-id="element-260" data-mp="s70q83k9u3ad">Picked from your Keys to match this event’s genres.</p>
                  <div className="mt-3 flex flex-wrap gap-2" data-id="element-261" data-mp="x3r4clcimot5">
                    {me.keys.map(k => <Chip key={k.id} selected={setlist.includes(k.id)} onClick={() => setSetlist(s => s.includes(k.id) ? s.filter(x => x !== k.id) : [...s, k.id])} data-id="element-262" data-mp="djsjpg1wqm4q">
                        {k.title}
                      </Chip>)}
                  </div>
                </div>
                <div className="flex gap-3" data-id="element-263" data-mp="igsfmy51m8i4">
                  <Button variant="ghost" onClick={() => setComposing(false)} data-id="element-264" data-mp="whwh5d8fmwqp">
                    Cancel
                  </Button>
                  <Button variant="accent" size="lg" fullWidth icon={<SendIcon className="h-4 w-4" data-id="element-266" data-mp="seqba6p8f7kr" />} onClick={submit} data-id="element-265" data-mp="sfhzuxkew51j">
                    Send application
                  </Button>
                </div>
              </motion.div> : <motion.div key="cta" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15
        }} className="flex gap-3" data-id="element-267" data-mp="11b7gktax0b8l">
                <Button variant="outline" size="lg" aria-pressed={isSaved} icon={<motion.span key={String(isSaved)} initial={{
            scale: 0.6
          }} animate={{
            scale: 1
          }} transition={spring.bouncy} data-id="element-269" data-mp="t9x3iq2ri0d">
                      <StarIcon className={`h-5 w-5 ${isSaved ? 'fill-current text-brand-strong' : ''}`} data-id="element-270" data-mp="5u7e3rzcm60a" />
                    </motion.span>} onClick={() => toggleSave(event.id)} data-id="element-268" data-mp="1b8ltwk1jkjwo">
                  {isSaved ? 'Saved' : 'Save'}
                </Button>
                <Button variant="accent" size="lg" fullWidth onClick={() => setComposing(true)} data-id="element-271" data-mp="3e9hcap04ar2s">
                  Apply
                </Button>
              </motion.div>}
          </AnimatePresence>}
      </div>
    </div>;
}
function StatusBlock({
  icon,
  title,
  body
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return <div className="flex items-start gap-3 rounded-md bg-tint px-4 py-4" data-id="element-272" data-mp="2vhy6ybimf0xl">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand text-on-brand" data-id="element-273" data-mp="2vfvbhbyp8flh">{icon}</span>
      <div data-id="element-274" data-mp="18lrplzq16p37">
        <p className="font-bold" data-id="element-275" data-mp="18kyszrksdfys">{title}</p>
        <p className="mt-0.5 text-sm text-ink-2" data-id="element-276" data-mp="18m06s3t6sgk7">{body}</p>
      </div>
    </div>;
}